import React, { useState, useEffect } from 'react';
import { Webhook, Plus, Power, Key, Activity, Send, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import type { OutboundWebhook, WebhookDelivery } from '@zarin/types';
import { apiFetch } from '../lib/auth';

interface WebhookItem {
  id: string;
  url: string;
  eventTypes: string[];
  secret: string;
  isActive: boolean;
  failureCount: number;
  lastTriggeredAt?: string | undefined;
  createdAt: string;
}

const INITIAL_WEBHOOKS: WebhookItem[] = [];

const AVAILABLE_EVENTS = [
  { id: 'order.created', label: 'Order Created' },
  { id: 'order.paid', label: 'Order Paid' },
  { id: 'order.fulfilled', label: 'Order Fulfilled' },
  { id: 'order.cancelled', label: 'Order Cancelled' },
  { id: 'inventory.low', label: 'Inventory Low (< 5)' },
  { id: 'inventory.out', label: 'Inventory Out of Stock' },
  { id: 'customer.created', label: 'New Customer Registered' },
];

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState<WebhookItem[]>(INITIAL_WEBHOOKS);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>(['order.created', 'order.paid']);
  const [testStatus, setTestStatus] = useState<string | null>(null);

  useEffect(() => {
    async function loadWebhooks() {
      try {
        setLoading(true);
        const res = await apiFetch<{ data: any[] }>('/webhooks');
        if (Array.isArray(res.data)) {
          setWebhooks(res.data.map((w: any) => ({
            id: w.id,
            url: w.url,
            eventTypes: Array.isArray(w.event_types) ? w.event_types : (w.eventTypes || []),
            secret: w.secret || 'whsec_••••••••',
            isActive: Boolean(w.is_active ?? w.isActive ?? true),
            failureCount: w.failure_count || w.failureCount || 0,
            lastTriggeredAt: w.last_triggered_at || w.lastTriggeredAt,
            createdAt: w.created_at || w.createdAt || new Date().toISOString(),
          })));
        }
      } catch (err) {
        console.error('Failed to load webhooks:', err);
      } finally {
        setLoading(false);
      }
    }
    loadWebhooks();
  }, []);

  const toggleEvent = (eventId: string) => {
    setSelectedEvents(prev =>
      prev.includes(eventId) ? prev.filter(e => e !== eventId) : [...prev, eventId]
    );
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url || selectedEvents.length === 0) return;

    const newWh: WebhookItem = {
      id: `wh_${Date.now()}`,
      url: url.trim(),
      eventTypes: selectedEvents,
      secret: `whsec_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`,
      isActive: true,
      failureCount: 0,
      createdAt: new Date().toISOString(),
    };

    setWebhooks([newWh, ...webhooks]);
    setShowModal(false);
    setUrl('');
  };

  const toggleActive = (id: string) => {
    setWebhooks(prev => prev.map(w => w.id === id ? { ...w, isActive: !w.isActive } : w));
  };

  const handleSendTest = (id: string) => {
    setTestStatus(id);
    setTimeout(() => {
      setTestStatus(null);
    }, 2500);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Outbound Webhooks</h1>
          <p style={styles.subtitle}>Stream real-time store events, order transitions, and inventory alerts to external APIs</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowModal(true)}>
          <Plus size={16} />
          <span>Add Webhook Endpoint</span>
        </button>
      </div>

      <div style={styles.list}>
        {webhooks.length === 0 ? (
          <div style={{ padding: '60px 24px', textAlign: 'center', color: '#8A8A8E', background: '#1A1A1E', borderRadius: 8, border: '1px dashed #333' }}>
            <Webhook size={36} color="#555" style={{ margin: '0 auto 12px' }} />
            <p style={{ margin: 0 }}>
              {loading ? 'Loading webhook subscriptions…' : 'No webhook endpoints registered yet. Click "Add Webhook Endpoint" above to subscribe to store events.'}
            </p>
          </div>
        ) : (
          webhooks.map((wh) => (
            <div key={wh.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={styles.endpointInfo}>
                  <div style={styles.urlRow}>
                    <Webhook size={16} color="#C9A84C" />
                    <code style={styles.urlText}>{wh.url}</code>
                    <span style={styles.statusBadge(wh.isActive)}>
                      {wh.isActive ? 'ACTIVE' : 'DISABLED'}
                    </span>
                  </div>
                  <div style={styles.secretRow}>
                    <Key size={12} color="#71717A" />
                    <span style={{ fontSize: 11, color: '#71717A' }}>Signing Secret:</span>
                    <code style={styles.secretText}>{wh.secret}</code>
                  </div>
                </div>

                <div style={styles.cardActions}>
                  <button
                    onClick={() => handleSendTest(wh.id)}
                    style={styles.testBtn}
                    disabled={testStatus === wh.id}
                  >
                    {testStatus === wh.id ? (
                      <>
                        <CheckCircle2 size={13} color="#10B981" />
                        <span style={{ color: '#10B981' }}>200 OK Sent</span>
                      </>
                    ) : (
                      <>
                        <Send size={13} /> Test Ping
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => toggleActive(wh.id)}
                    style={styles.toggleBtn(wh.isActive)}
                  >
                    <Power size={13} />
                    {wh.isActive ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>

              <div style={styles.cardBottom}>
                <div style={styles.eventsRow}>
                  <span style={{ fontSize: 11, color: '#8A8A8E', marginRight: 4 }}>Subscribed:</span>
                  {wh.eventTypes.map(ev => (
                    <span key={ev} style={styles.eventTag}>{ev}</span>
                  ))}
                </div>

                <div style={styles.metaRow}>
                  {wh.failureCount > 0 ? (
                    <span style={styles.failNotice}>
                      <AlertTriangle size={12} /> {wh.failureCount} recent failed attempts
                    </span>
                  ) : (
                    <span style={styles.healthyNotice}>
                      <CheckCircle2 size={12} /> 100% Delivery Health
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: '#71717A' }}>
                    Last triggered: {wh.lastTriggeredAt ? new Date(wh.lastTriggeredAt).toLocaleTimeString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Configure Outbound Webhook</h3>
            <p style={styles.modalSubtitle}>Provide your HTTPS receiver URL and choose topics to subscribe to</p>

            <form onSubmit={handleCreate} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Endpoint URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  style={styles.input}
                  placeholder="https://api.yourdomain.com/webhooks/zarin"
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Subscribed Events</label>
                <div style={styles.checkboxGrid}>
                  {AVAILABLE_EVENTS.map(ev => (
                    <label key={ev.id} style={styles.checkLabel}>
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(ev.id)}
                        onChange={() => toggleEvent(ev.id)}
                        style={{ accentColor: '#C9A84C' }}
                      />
                      <span style={{ fontSize: 12, color: '#D4D4D8' }}>{ev.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn}>
                  Save Webhook
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  page: {
    padding: '28px 32px',
    maxWidth: 1200,
    margin: '0 auto',
    color: '#E5E5E5',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 26,
    fontWeight: 600,
    color: '#FFFFFF',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#8A8A8E',
    marginTop: 6,
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#C9A84C',
    color: '#0A0A0A',
    border: 'none',
    padding: '9px 16px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  card: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    padding: 20,
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 12,
    borderBottom: '1px solid #1F1F1F',
    paddingBottom: 14,
    marginBottom: 14,
  },
  endpointInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },
  urlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    wordBreak: 'break-all',
  },
  urlText: {
    fontSize: 14,
    fontFamily: 'monospace',
    color: '#FFF',
    background: '#1C1C1E',
    padding: '3px 8px',
    borderRadius: 4,
  },
  secretRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  secretText: {
    fontSize: 11,
    fontFamily: 'monospace',
    color: '#A1A1AA',
    background: '#1A1A1A',
    padding: '2px 6px',
    borderRadius: 3,
  },
  statusBadge: (active: boolean) => ({
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.05em',
    padding: '2px 8px',
    borderRadius: 4,
    background: active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
    color: active ? '#10B981' : '#EF4444',
  }),
  cardActions: {
    display: 'flex',
    gap: 8,
  },
  testBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#1F1F1F',
    border: '1px solid #333',
    color: '#E5E5E5',
    padding: '6px 12px',
    borderRadius: 5,
    fontSize: 12,
    cursor: 'pointer',
  },
  toggleBtn: (active: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: active ? '#27272A' : 'rgba(16, 185, 129, 0.2)',
    color: active ? '#A1A1AA' : '#10B981',
    border: 'none',
    padding: '6px 12px',
    borderRadius: 5,
    fontSize: 12,
    cursor: 'pointer',
  }),
  cardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  eventsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  eventTag: {
    fontSize: 11,
    fontFamily: 'monospace',
    background: '#1E1E1E',
    color: '#C9A84C',
    padding: '2px 8px',
    borderRadius: 4,
    border: '1px solid #333',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  healthyNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    color: '#10B981',
  },
  failNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    color: '#EF4444',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#141414',
    border: '1px solid #2A2A2A',
    borderRadius: 10,
    width: 500,
    maxWidth: '95vw',
    padding: 24,
  },
  modalTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 20,
    fontWeight: 600,
    color: '#FFF',
    margin: 0,
  },
  modalSubtitle: {
    fontSize: 12,
    color: '#8A8A8E',
    marginTop: 4,
    marginBottom: 20,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: '#A1A1AA',
  },
  input: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
    background: '#1B1B1D',
    padding: 12,
    borderRadius: 6,
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 10,
  },
  cancelBtn: {
    background: '#27272A',
    border: 'none',
    borderRadius: 6,
    padding: '8px 16px',
    color: '#CCC',
    fontSize: 13,
    cursor: 'pointer',
  },
  submitBtn: {
    background: '#C9A84C',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    color: '#0A0A0A',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
};
